import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Per-bot action state — lightweight, user-toggled bot states that
 * aren't part of the bot's core data record (`Bot` in mock-data).
 * Driven by the ⋯ actions sheet in the bot detail drawer (Phase 6,
 * Sergey 2026-05-21).
 *
 * `watchedIds`
 * ------------
 * Bots the user is "watching" on a paper account (⋯ → Watch). Watching
 * mirrors the bot onto a paper account so the user can evaluate it
 * without committing real capital. Toggled back off via ⋯ → Unwatch.
 *
 * `skipNextIds`
 * -------------
 * Bots flagged to skip their next scheduled rebalance (⋯ → Skip
 * trading). The flag is advisory in this build — there's no live
 * scheduler wired yet — but the UI reflects it so the user can see
 * the choice and undo it.
 *
 * Persisted to localStorage under `whb-bot-actions`.
 */
interface BotActionsState {
  watchedIds: string[];
  skipNextIds: string[];
  /** Toggle paper-watching for a bot. */
  toggleWatched: (id: string) => void;
  /** Toggle the skip-next-rebalance flag for a bot. */
  toggleSkipNext: (id: string) => void;
}

export const useBotActionsStore = create<BotActionsState>()(
  persist(
    (set) => ({
      watchedIds: [],
      skipNextIds: [],
      toggleWatched: (id) =>
        set((s) => ({
          watchedIds: s.watchedIds.includes(id)
            ? s.watchedIds.filter((x) => x !== id)
            : [...s.watchedIds, id],
        })),
      toggleSkipNext: (id) =>
        set((s) => ({
          skipNextIds: s.skipNextIds.includes(id)
            ? s.skipNextIds.filter((x) => x !== id)
            : [...s.skipNextIds, id],
        })),
    }),
    {
      name: "whb-bot-actions",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
